import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "./api";
import { useAuth } from "./QcApp";

const Shell = ({ children, title, sub }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#F9F8F6] px-4">
    <div className="w-full max-w-sm">
      <div className="mb-8 flex flex-col items-center">
        <img src="/snf-logo.png" alt="Sun Nutrafoods" className="h-14 w-auto" />
        <h1 className="mt-4 font-display text-2xl font-semibold text-[#1C3F2B]">{title}</h1>
        <p className="mt-1 text-center text-sm text-[#8A9A91]">{sub}</p>
      </div>
      <div className="rounded-2xl border border-[#E8E6E1] bg-white p-7 shadow-[0_8px_32px_rgba(28,63,43,0.06)]">
        {children}
      </div>
      <p className="mt-6 text-center text-xs text-[#8A9A91]">
        <a href="/" className="underline underline-offset-2 hover:text-[#1C3F2B]">← Back to portal</a>
      </p>
    </div>
  </div>
);

export function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("snf_qc_token", data.token);
      setUser(data.user);
      navigate("/qc/home");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Quality Control" sub="Sign in with your QC portal account">
      <form onSubmit={submit} className="space-y-4" data-testid="qc-login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="qc-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="qc-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <Button data-testid="qc-login-submit" type="submit" disabled={busy} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <p className="text-center text-sm">
          <Link to="/qc/forgot" data-testid="qc-forgot-link" className="text-[#8A9A91] underline underline-offset-2 hover:text-[#1C3F2B]">
            Forgot password?
          </Link>
        </p>
      </form>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Reset password" sub="Recovery depends on your role">
      {done ? (
        <div data-testid="qc-forgot-done" className="space-y-4 text-sm text-[#1C3F2B]">
          <p>If this account exists, the appropriate action has been taken:</p>
          <ul className="list-disc space-y-1 pl-5 text-[#8A9A91]">
            <li>Admins receive a self-service reset link by email.</li>
            <li>For all other roles, every Admin is notified and will set a new password for you.</li>
          </ul>
          <Link to="/qc/login" className="block text-center text-[#1C3F2B] underline underline-offset-2">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="qc-forgot-form">
          <div className="space-y-1.5">
            <Label htmlFor="femail">Email</Label>
            <Input id="femail" data-testid="qc-forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <Button data-testid="qc-forgot-submit" type="submit" disabled={busy} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            {busy ? "Sending…" : "Request reset"}
          </Button>
          <p className="text-center text-sm">
            <Link to="/qc/login" className="text-[#8A9A91] underline underline-offset-2 hover:text-[#1C3F2B]">Back to sign in</Link>
          </p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPage() {
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Choose a new password" sub="This link works once and expires in 1 hour">
      {done ? (
        <div data-testid="qc-reset-done" className="space-y-4 text-center text-sm text-[#1C3F2B]">
          <p>Your password has been updated.</p>
          <Link to="/qc/login" className="text-[#1C3F2B] underline underline-offset-2">Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="qc-reset-form">
          <div className="space-y-1.5">
            <Label htmlFor="pw1">New password</Label>
            <Input id="pw1" data-testid="qc-reset-password" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw2">Confirm new password</Label>
            <Input id="pw2" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </div>
          <Button data-testid="qc-reset-submit" type="submit" disabled={busy} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            {busy ? "Saving…" : "Reset password"}
          </Button>
        </form>
      )}
    </Shell>
  );
}
